import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Bell,
  CircleCheckBig,
  CircleX,
  Gamepad2,
  Medal,
  Mic,
  MicOff,
  Play,
  SkipForward,
  Square,
  Trophy,
  UserPlus,
  Zap,
} from 'lucide-react';
import { io, Socket } from 'socket.io-client';

type SessionStatus = 'WAITING' | 'ACTIVE' | 'FINISHED' | 'CANCELLED';

type SessionState = {
  sessionId: string;
  status: SessionStatus;
  playersCount: number;
  currentRound: number;
};

type QuestionOption = { id: string; text: string };

type LiveQuestion = {
  sessionId: string;
  questionId: string;
  roundNumber: number;
  difficulty: 'EASY' | 'MEDIUM' | 'HARD';
  timeLimitSec: number;
  prompt: string;
  options: QuestionOption[];
  serverStartTs: number;
  serverEndTs: number;
};

type RankingEntry = {
  rank: number;
  playerId: string;
  displayName: string;
  points: number;
  correctCount: number;
  answersCount: number;
};

type JoinResponse = {
  sessionId: string;
  playerId: string;
  displayName: string;
  identityKey: string;
  playerToken: string;
};

type GameSessionCreate = {
  sessionId: string;
  joinCode: string;
  shareLink: string;
  status: SessionStatus;
};

type ToastTone = 'info' | 'success' | 'warn' | 'danger';

type ToastItem = {
  id: number;
  tone: ToastTone;
  title: string;
  body?: string;
};

interface GamesTabProps {
  apiUrl: string;
  eventId: string;
  eventTitle: string;
  sessionToken: string;
  leaderName: string;
}

function getToneClasses(tone: ToastTone): string {
  if (tone === 'success') return 'border-emerald-200 bg-emerald-50 text-emerald-800';
  if (tone === 'warn') return 'border-amber-200 bg-amber-50 text-amber-800';
  if (tone === 'danger') return 'border-rose-200 bg-rose-50 text-rose-800';
  return 'border-sky-200 bg-sky-50 text-sky-800';
}

export default function GamesTab({
  apiUrl,
  eventId,
  eventTitle,
  sessionToken,
  leaderName,
}: GamesTabProps) {
  const socketRef = useRef<Socket | null>(null);
  const audioRef = useRef<AudioContext | null>(null);
  const toastCounterRef = useRef(1);

  const [isBusy, setIsBusy] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);

  const [session, setSession] = useState<GameSessionCreate | null>(null);
  const [state, setState] = useState<SessionState | null>(null);
  const [question, setQuestion] = useState<LiveQuestion | null>(null);
  const [remainingMs, setRemainingMs] = useState(0);
  const [ranking, setRanking] = useState<RankingEntry[]>([]);
  const [reviewUrl, setReviewUrl] = useState<string | null>(null);

  const [playerName, setPlayerName] = useState('');
  const [playerPhone, setPlayerPhone] = useState('');
  const [player, setPlayer] = useState<JoinResponse | null>(null);
  const [selectedOptionId, setSelectedOptionId] = useState('');

  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const canStart = session && state?.status === 'WAITING';
  const canAdvance = session && state?.status === 'ACTIVE';

  const apiBase = useMemo(() => String(apiUrl || '').replace(/\/$/, ''), [apiUrl]);

  const pushToast = (tone: ToastTone, title: string, body?: string) => {
    const id = toastCounterRef.current++;
    const item: ToastItem = { id, tone, title, body };
    setToasts((prev) => [item, ...prev].slice(0, 6));
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, 4200);
  };

  const maybeNotify = async (title: string, body: string) => {
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    if (Notification.permission === 'granted') {
      new Notification(title, { body });
      return;
    }
    if (Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        new Notification(title, { body });
      }
    }
  };

  const getAudioContext = (): AudioContext | null => {
    if (!isAudioEnabled || typeof window === 'undefined') return null;
    if (!audioRef.current) {
      const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!Ctx) return null;
      audioRef.current = new Ctx();
    }
    return audioRef.current;
  };

  const playTone = (frequency: number, durationSec: number, gain: number) => {
    const ctx = getAudioContext();
    if (!ctx) return;

    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();

    oscillator.type = 'sine';
    oscillator.frequency.value = frequency;
    gainNode.gain.value = gain;

    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    oscillator.start();
    gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + durationSec);
    oscillator.stop(ctx.currentTime + durationSec);
  };

  const playSuccessChord = () => {
    playTone(523.25, 0.18, 0.08);
    window.setTimeout(() => playTone(659.25, 0.2, 0.07), 70);
    window.setTimeout(() => playTone(783.99, 0.22, 0.07), 140);
  };

  const playWarningBeep = () => {
    playTone(220, 0.12, 0.06);
  };

  const requestGameApi = async (path: string, init?: RequestInit) => {
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    const candidates = normalizedPath.startsWith('/chatbot/')
      ? [`${apiBase}${normalizedPath}`]
      : [`${apiBase}${normalizedPath}`, `${apiBase}/api/v1${normalizedPath}`];
    let lastError: Error | null = null;
    let bestHttpError: Error | null = null;

    const isLikelyMissingRoute = (message: string) => {
      const normalized = String(message || '').toLowerCase();
      return (
        normalized.includes('cannot get') ||
        normalized.includes('cannot post') ||
        normalized.includes('cannot put') ||
        normalized.includes('cannot patch') ||
        normalized.includes('cannot delete') ||
        normalized.includes('route') ||
        normalized === 'not found'
      );
    };

    for (const url of candidates) {
      try {
        const response = await fetch(url, init);
        const data = await response
          .json()
          .catch(() => ({ message: `Unexpected response from ${url}` }));

        if (response.status === 404) {
          const message = String(data?.message || 'Not found');
          const routeMissing = isLikelyMissingRoute(message);

          if (routeMissing) {
            continue;
          }

          throw new Error(message);
        }

        if (!response.ok) {
          throw new Error(data?.message || `Request failed (${response.status})`);
        }

        return data;
      } catch (error) {
        const normalized = error instanceof Error ? error : new Error(String(error));
        const message = String(normalized.message || '').toLowerCase();
        const isNetworkError =
          message.includes('failed to fetch') ||
          message.includes('networkerror') ||
          message.includes('load failed');

        // Keep business/auth/validation errors as the preferred message for the user.
        if (!isNetworkError) {
          bestHttpError = normalized;
        }

        lastError = normalized;
      }
    }

    throw bestHttpError || lastError || new Error('Game API is unavailable');
  };

  useEffect(() => {
    if (!question) {
      setRemainingMs(0);
      return;
    }

    const update = () => {
      setRemainingMs(Math.max(0, question.serverEndTs - Date.now()));
    };

    update();
    const timer = window.setInterval(update, 120);

    return () => window.clearInterval(timer);
  }, [question]);

  useEffect(() => {
    if (!session?.sessionId) return;

    const socket = io(apiBase, {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      setIsConnected(true);
      socket.emit('game.join_room', { sessionId: session.sessionId });
      pushToast('success', 'Realtime conectado', 'Sala de jogo pronta para transmissão.');
      playSuccessChord();
    });

    socket.on('disconnect', () => {
      setIsConnected(false);
      pushToast('warn', 'Realtime desconectado', 'Tentando reconectar automaticamente...');
      playWarningBeep();
    });

    socket.on('game.session_state', (payload: SessionState) => {
      setState(payload);
    });

    socket.on('game.question_started', async (payload: LiveQuestion) => {
      setQuestion(payload);
      setSelectedOptionId('');
      pushToast('info', `Pergunta ${payload.roundNumber} iniciada`, payload.difficulty);
      playTone(440, 0.14, 0.06);
      await maybeNotify('Nova pergunta no quiz', `Rodada ${payload.roundNumber}: ${payload.difficulty}`);
    });

    socket.on('game.answer_ack', (payload: { accepted: boolean; reason?: string }) => {
      if (payload.accepted) {
        pushToast('success', 'Resposta enviada', 'Seu palpite foi registrado.');
        playTone(640, 0.12, 0.05);
      } else {
        pushToast('warn', 'Resposta rejeitada', payload.reason || 'Nao foi possivel registrar.');
        playWarningBeep();
      }
    });

    socket.on('game.round_closed', (payload: any) => {
      pushToast('info', 'Rodada encerrada', `Correta: ${payload.correctOptionId}`);
      playTone(320, 0.18, 0.05);
    });

    socket.on('game.partial_ranking', (payload: { entries: RankingEntry[] }) => {
      setRanking(payload.entries || []);
    });

    socket.on('game.final_ranking', async (payload: { entries: RankingEntry[] }) => {
      setRanking(payload.entries || []);
      pushToast('success', 'Partida finalizada', 'Ranking final publicado com sucesso.');
      playSuccessChord();
      await maybeNotify('Quiz finalizado', 'Ranking final disponivel.');
    });

    socket.on('game.review_ready', (payload: { reviewUrl: string }) => {
      setReviewUrl(payload.reviewUrl);
      pushToast('success', 'Review liberado', 'Voce ja pode revisar as perguntas.');
    });

    socket.on('game.error', (payload: { message?: string; code?: string }) => {
      pushToast('danger', payload.code || 'Erro do jogo', payload.message || 'Falha na operacao');
      playWarningBeep();
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
      setIsConnected(false);
    };
  }, [apiBase, session?.sessionId]);

  const createSession = async () => {
    setIsBusy(true);
    try {
      const created = await requestGameApi(`/chatbot/events/${eventId}/games`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-session-token': sessionToken,
        },
        body: JSON.stringify({
          totalQuestions: 10,
          progressionMode: 'FIXED',
          baselineDifficulty: 'EASY',
          allowedThemes: ['Faith', 'Grace', 'Hope', 'Love'],
          reviewEnabled: true,
        }),
      });

      setSession(created);
      setState({
        sessionId: created.sessionId,
        status: created.status || 'WAITING',
        playersCount: 0,
        currentRound: 0,
      });
      setRanking([]);
      setReviewUrl(null);
      setPlayer(null);
      pushToast('success', 'Sessao criada', `Codigo de entrada: ${created.joinCode}`);
      playSuccessChord();
    } catch (error) {
      pushToast('danger', 'Falha ao criar sessao', (error as Error).message);
    } finally {
      setIsBusy(false);
    }
  };

  const emitWithSession = (eventName: string) => {
    if (!socketRef.current || !session?.sessionId) {
      pushToast('warn', 'Realtime indisponivel', 'Crie uma sessao e aguarde conexao.');
      return;
    }
    socketRef.current.emit(eventName, { sessionId: session.sessionId });
  };

  const handleJoinAsPlayer = async () => {
    if (!session?.joinCode) {
      pushToast('warn', 'Sem codigo', 'Crie a sessao antes de entrar como jogador.');
      return;
    }

    if (!playerName.trim()) {
      pushToast('warn', 'Nome obrigatorio', 'Informe um nome para entrar na partida.');
      return;
    }

    setIsBusy(true);
    try {
      const joined = await requestGameApi('/games/join', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: session.joinCode,
          name: playerName.trim(),
          phone: playerPhone.trim() || undefined,
        }),
      });

      setPlayer(joined);
      pushToast('success', 'Jogador conectado', `Boa partida, ${joined.displayName}.`);
      playTone(560, 0.15, 0.06);
    } catch (error) {
      pushToast('danger', 'Falha ao entrar', (error as Error).message);
    } finally {
      setIsBusy(false);
    }
  };

  const submitAnswer = () => {
    if (!socketRef.current || !session?.sessionId || !question?.questionId || !player?.playerId) {
      pushToast('warn', 'Nao foi possivel responder', 'Entre como jogador e aguarde uma pergunta ativa.');
      return;
    }

    if (!selectedOptionId) {
      pushToast('warn', 'Selecione uma opcao', 'Marque uma alternativa antes de enviar.');
      return;
    }

    socketRef.current.emit('game.submit_answer', {
      sessionId: session.sessionId,
      questionId: question.questionId,
      playerId: player.playerId,
      selectedOptionId,
      clientTs: Date.now(),
    });
  };

  const countdownSec = Math.ceil(remainingMs / 1000);

  return (
    <div className="space-y-4">
      <section className="game-gradient rounded-2xl border border-cyan-200 p-4 shadow-card">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Quiz Realtime</h3>
            <p className="text-sm text-slate-600">
              Evento: <span className="font-medium text-slate-800">{eventTitle}</span>
            </p>
            <p className="text-xs text-slate-500">Host: {leaderName}</p>
          </div>

          <div className="flex items-center gap-2">
            <span
              className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-xs font-semibold ${
                isConnected
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                  : 'border-amber-200 bg-amber-50 text-amber-700'
              }`}
            >
              <Bell className="h-3.5 w-3.5" />
              {isConnected ? 'Realtime ON' : 'Realtime OFF'}
            </span>

            <button
              type="button"
              onClick={() => setIsAudioEnabled((prev) => !prev)}
              className="inline-flex items-center gap-1 rounded-xl border border-slate-200 bg-white px-2.5 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50"
            >
              {isAudioEnabled ? <Mic className="h-4 w-4" /> : <MicOff className="h-4 w-4" />}
              {isAudioEnabled ? 'Audio ON' : 'Audio OFF'}
            </button>
          </div>
        </div>
      </section>

      <section className="grid gap-4 xl:grid-cols-2">
        <article className="rounded-2xl border border-slate-200 bg-white p-4 shadow-card">
          <h4 className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-800">
            <Gamepad2 className="h-4 w-4 text-cyan-700" />
            Controle da Partida
          </h4>

          {!session ? (
            <button
              type="button"
              onClick={createSession}
              disabled={isBusy}
              className="inline-flex h-11 items-center gap-2 rounded-xl bg-cyan-700 px-4 text-sm font-semibold text-white hover:bg-cyan-800 disabled:cursor-not-allowed disabled:opacity-70"
            >
              <Zap className="h-4 w-4" />
              {isBusy ? 'Criando...' : 'Criar Sessao'}
            </button>
          ) : (
            <div className="space-y-3">
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm">
                <p>
                  Codigo: <span className="font-bold tracking-widest text-cyan-800">{session.joinCode}</span>
                </p>
                <p>
                  Status: <span className="font-semibold text-slate-700">{state?.status || session.status}</span>
                </p>
                <p>
                  Rodada: <span className="font-semibold text-slate-700">{state?.currentRound || 0}</span>
                </p>
                <p>
                  Jogadores: <span className="font-semibold text-slate-700">{state?.playersCount || 0}</span>
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => emitWithSession('game.start')}
                  disabled={!canStart}
                  className="inline-flex h-10 items-center gap-1 rounded-xl border border-emerald-200 bg-emerald-50 px-3 text-sm font-semibold text-emerald-700 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Play className="h-4 w-4" />
                  Iniciar
                </button>

                <button
                  type="button"
                  onClick={() => emitWithSession('game.next_question')}
                  disabled={!canAdvance}
                  className="inline-flex h-10 items-center gap-1 rounded-xl border border-sky-200 bg-sky-50 px-3 text-sm font-semibold text-sky-700 hover:bg-sky-100 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <SkipForward className="h-4 w-4" />
                  Proxima
                </button>

                <button
                  type="button"
                  onClick={() => emitWithSession('game.end')}
                  disabled={!session}
                  className="inline-flex h-10 items-center gap-1 rounded-xl border border-rose-200 bg-rose-50 px-3 text-sm font-semibold text-rose-700 hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Square className="h-4 w-4" />
                  Encerrar
                </button>
              </div>

              {reviewUrl && (
                <a
                  href={`${apiBase}${reviewUrl}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex text-sm font-medium text-cyan-700 underline decoration-dotted underline-offset-4"
                >
                  Abrir review da partida
                </a>
              )}
            </div>
          )}
        </article>

        <article className="rounded-2xl border border-slate-200 bg-white p-4 shadow-card">
          <h4 className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-800">
            <UserPlus className="h-4 w-4 text-indigo-700" />
            Jogador de Teste (Local)
          </h4>

          <div className="grid gap-2 md:grid-cols-2">
            <input
              value={playerName}
              onChange={(event) => setPlayerName(event.target.value)}
              placeholder="Nome do jogador"
              className="h-10 rounded-xl border border-slate-200 px-3 text-sm"
            />
            <input
              value={playerPhone}
              onChange={(event) => setPlayerPhone(event.target.value)}
              placeholder="Telefone (opcional)"
              className="h-10 rounded-xl border border-slate-200 px-3 text-sm"
            />
          </div>

          <button
            type="button"
            onClick={handleJoinAsPlayer}
            disabled={!session || isBusy}
            className="mt-2 inline-flex h-10 items-center gap-1 rounded-xl border border-indigo-200 bg-indigo-50 px-3 text-sm font-semibold text-indigo-700 hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <UserPlus className="h-4 w-4" />
            Entrar com Codigo
          </button>

          {player && (
            <p className="mt-2 text-xs text-slate-600">
              Jogador ativo: <span className="font-semibold">{player.displayName}</span>
            </p>
          )}
        </article>
      </section>

      <section className="grid gap-4 xl:grid-cols-5">
        <article className="xl:col-span-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-card">
          <div className="mb-2 flex items-center justify-between">
            <h4 className="text-sm font-semibold text-slate-800">Pergunta Atual</h4>
            <span className="game-timer inline-flex min-w-[68px] items-center justify-center rounded-full border border-amber-200 bg-amber-50 px-2 py-1 text-xs font-bold text-amber-700">
              {countdownSec}s
            </span>
          </div>

          {!question ? (
            <p className="text-sm text-slate-500">Aguardando inicio de rodada...</p>
          ) : (
            <div className="space-y-3">
              <div className="rounded-xl border border-cyan-100 bg-cyan-50 p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-cyan-700">
                  Rodada {question.roundNumber} - {question.difficulty}
                </p>
                <p className="mt-1 text-sm font-medium text-slate-800">{question.prompt}</p>
              </div>

              <div className="grid gap-2 md:grid-cols-2">
                {question.options.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => setSelectedOptionId(option.id)}
                    className={`rounded-xl border p-3 text-left text-sm transition ${
                      selectedOptionId === option.id
                        ? 'game-choice-selected border-cyan-500 bg-cyan-50 text-cyan-800'
                        : 'border-slate-200 bg-white text-slate-700 hover:border-cyan-300 hover:bg-cyan-50/50'
                    }`}
                  >
                    <p className="font-semibold">{option.id}</p>
                    <p className="mt-1 text-xs">{option.text}</p>
                  </button>
                ))}
              </div>

              <button
                type="button"
                onClick={submitAnswer}
                className="inline-flex h-10 items-center gap-2 rounded-xl bg-cyan-700 px-3 text-sm font-semibold text-white hover:bg-cyan-800"
              >
                <CircleCheckBig className="h-4 w-4" />
                Enviar Resposta
              </button>
            </div>
          )}
        </article>

        <article className="xl:col-span-2 rounded-2xl border border-slate-200 bg-white p-4 shadow-card">
          <h4 className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-800">
            <Trophy className="h-4 w-4 text-amber-600" />
            Ranking Live
          </h4>

          {ranking.length === 0 ? (
            <p className="text-sm text-slate-500">Sem pontuacao ainda.</p>
          ) : (
            <div className="space-y-2">
              {ranking.slice(0, 8).map((entry) => (
                <div
                  key={entry.playerId}
                  className={`flex items-center justify-between rounded-xl border px-3 py-2 text-sm ${
                    entry.rank === 1
                      ? 'game-rank-top border-amber-200 bg-amber-50'
                      : 'border-slate-200 bg-white'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {entry.rank <= 3 ? (
                      <Medal className={`h-4 w-4 ${entry.rank === 1 ? 'text-amber-500' : entry.rank === 2 ? 'text-slate-500' : 'text-orange-500'}`} />
                    ) : (
                      <span className="w-4 text-center text-xs font-bold text-slate-500">{entry.rank}</span>
                    )}
                    <span className="font-medium text-slate-800">{entry.displayName}</span>
                  </div>
                  <span className="font-bold text-cyan-800">{entry.points}</span>
                </div>
              ))}
            </div>
          )}
        </article>
      </section>

      <section className="space-y-2">
        {toasts.map((toast) => (
          <div key={toast.id} className={`rounded-xl border px-3 py-2 text-sm ${getToneClasses(toast.tone)}`}>
            <p className="font-semibold">{toast.title}</p>
            {toast.body ? <p className="text-xs opacity-90">{toast.body}</p> : null}
          </div>
        ))}
      </section>
    </div>
  );
}
